import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import './LandingPage.css';

interface JoinWaitlistModalProps {
  onClose: () => void;
  tools: Tool[];
}

// A simple modal component for the waiting list form
const JoinWaitlistModal: React.FC<JoinWaitlistModalProps> = ({ onClose, tools }) => {
  const [email, setEmail] = useState('');
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "YOUR_RECAPTCHA_SITE_KEY_HERE";
  
  const handleToolSelection = (toolTitle: string) => {
    setSelectedTools(prev => 
      prev.includes(toolTitle) 
        ? prev.filter(t => t !== toolTitle)
        : [...prev, toolTitle]
    );
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (!recaptchaRef.current) {
        setIsSubmitting(false);
        return;
    }

    const token = await recaptchaRef.current.executeAsync();
    
    if (!token) {
      alert('No se pudo verificar el reCAPTCHA. Por favor, inténtalo de nuevo.');
      setIsSubmitting(false);
      return;
    }
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/join-waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          recaptcha_token: token,
          selected_tools: selectedTools,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Algo salió mal. Por favor, inténtalo de nuevo.');
      }

      alert('¡Gracias por unirte a nuestra lista de espera!');
      onClose();
    } catch (error) {
        setIsSubmitting(false);
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert('Ocurrió un error inesperado.');
      }
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h2>Únete a la lista de espera</h2>
        <p>Deja tu correo electrónico y te avisaremos en orden de llegada.</p>
        <form onSubmit={handleFormSubmit}>
          <input 
            type="email" 
            placeholder="tu@email.com" 
            required 
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <div className="tools-checklist">
            <h4>¿Qué herramientas te interesan más?</h4>
            {tools.map(tool => (
              <div key={tool.title} className="checklist-item">
                <input 
                  type="checkbox" 
                  id={tool.title} 
                  value={tool.title} 
                  onChange={() => handleToolSelection(tool.title)}
                />
                <label htmlFor={tool.title}>{tool.title}</label>
              </div>
            ))}
          </div>
          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            sitekey={siteKey}
          />
          <button type="submit" className="btn" disabled={isSubmitting}>
            {isSubmitting ? <span className="spinner" /> : 'Notifícame'}
          </button>
        </form>
        <button onClick={onClose} className="close-modal-btn">&times;</button>
      </div>
    </div>
  );
};

interface ToolDetailsModalProps {
    tool: Tool;
    onClose: () => void;
}

const ToolDetailsModal: React.FC<ToolDetailsModalProps> = ({ tool, onClose }) => {
    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content tool-modal-content" onClick={e => e.stopPropagation()}>
                <h2>{tool.title}</h2>
                <p><i>Más detalles sobre esta herramienta próximamente...</i></p>
                <button onClick={onClose} className="close-modal-btn">&times;</button>
            </div>
        </div>
    );
};

const tools = [
  {
    title: 'MatrixGen',
    description: 'Generación y gestión automática de matrices de riesgo a la medida utilizando modelos de lenguaje.',
    imageUrl: '/logos/matrixgen04.jpeg',
  },
  {
    title: 'Compliance ChatBot',
    description: 'Dota a tu empresa con un agente experto en cumplimiento que responda las dudas de tus colaboradores.',
    imageUrl: '/logos/chatbot01.jpeg',
  },
  {
    title: 'Analizador de Contratos',
    description: 'Analiza las cláusulas de tus contratos y evalúa el riesgo que significan para tus operaciones.',
    imageUrl: '/logos/contractanalyzer01.jpeg',
  },
  {
    title: 'Deadline Agent',
    description: 'Sistema de alerta temprana para cumplir con fechas regulatorias.',
    imageUrl: '/logos/deadline01.jpeg',
  },
  {
    title: 'Investigación y Análisis',
    description: 'Identifica precedentes y analiza casos específicos en cuanto a la ley vigente.',
    imageUrl: '/logos/research01.jpeg',
  },
  {
    title: 'Simulador de Cumplimiento',
    description: 'Entrena a tus colaboradores con dilemas de cumplimiento realistas generados por IA para una capacitación interactiva.',
    imageUrl: '/logos/simulator.jpeg',
  },
  {
    title: 'PolicyCraft',
    description: 'Genera, actualiza y gestiona automáticamente las políticas de cumplimiento internas de tu empresa.',
    imageUrl: '/logos/polycraft03.jpeg',
  },
  {
    title: 'SecureChannel AI',
    description: 'Gestiona tu canal de denuncias anónimas con IA para asegurar la protección y guiar la investigación interna.',
    imageUrl: '/logos/securechannel.jpeg',
  },
];

interface Tool {
  title: string;
  description: string;
  imageUrl: string;
}

interface ToolCardProps {
  tool: Tool;
  onSaberMas: (tool: Tool) => void;
}

const ToolCard: React.FC<ToolCardProps> = ({ tool, onSaberMas }) => {
  return (
    <div className="card" style={{ '--bg-image': `url(${tool.imageUrl})` } as React.CSSProperties}>
      <div className="content">
        <h2 className="title">{tool.title}</h2>
        <p className="copy">{tool.description}</p>
        <button className="btn" onClick={() => onSaberMas(tool)}>Saber más</button>
      </div>
    </div>
  );
};

function LandingPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);

  const handleLogin = () => {
    setIsLoading(true);
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    window.location.href = `${apiUrl}/auth/login`;
  };

  const handleSaberMas = (tool: Tool) => {
    setSelectedTool(tool);
  };

  return (
    <div className="landing-page">
      <nav className="top-nav">
        <div className="logo-container">
            <img src="logoo.png" alt="Logo" className="logo" />
        </div>
        <button onClick={handleLogin} className="login-button-nav" disabled={isLoading}>
          {isLoading ? <span className="spinner-small" /> : 'Iniciar Sesión'}
        </button>
      </nav>

      <header className="landing-header">
        <h1>Tu Navaja Suiza de Cumplimiento</h1>
        <p className="subtitle">por <a href="https://octopuslabs.co" target="_blank" rel="noopener noreferrer">OctopusLabs LLC</a> en colaboracion con <a href="https://www.nawrath.cl" target="_blank" rel="noopener noreferrer">Nawrath Consultores</a></p>
        <p className="description">
          Automatiza y facilita la gestión de compliance de tu empresa con Inteligencia Artificial.
        </p>
        <button onClick={() => setShowWaitlistModal(true)} className="cta-button">
            Únete a la Lista de Espera
        </button>
      </header>

      <main className="page-content">
        {tools.map((tool) => (
          <ToolCard key={tool.title} tool={tool} onSaberMas={handleSaberMas} />
        ))}
      </main>

      <section className="about-section">
        <div className="about-container">
          <div className="about-content">
            <div className="about-column">
              <h3>Nuestra Visión</h3>
              <p>
                Ser la plataforma líder en Latinoamérica que transforma la gestión de cumplimiento normativo a través de la <b>inteligencia artificial</b>, haciendo que sea accesible, proactiva y un pilar estratégico para empresas de todos los tamaños.
              </p>
            </div>
            <div className="about-column">
              <h3>Nuestra Misión</h3>
              <p>
                Potenciar a las organizaciones con herramientas inteligentes e intuitivas que <b>simplifican la complejidad del compliance</b>, minimizan riesgos y fomentan una cultura de integridad, permitiéndoles enfocarse en crecer de manera <b>segura y sostenible</b>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <p>&copy; {new Date().getFullYear()} OctopusLabs LLC</p>
      </footer>

      {showWaitlistModal && <JoinWaitlistModal onClose={() => setShowWaitlistModal(false)} tools={tools} />}
      {selectedTool && <ToolDetailsModal tool={selectedTool} onClose={() => setSelectedTool(null)} />}
    </div>
  );
}

export default LandingPage; 